import { getSupabase } from "@/lib/supabase/client";
import {
 slugForProgramDay,
 themeForProgramDay,
} from "@/features/programs/data/program-days";
import {
 assignExperiment,
 listPublishedExperiments,
} from "@/features/experiments/api/experiments-api";
import type { Experiment, ExperimentAssignment } from "@andrum/schemas";
import { t } from "@/i18n";

export type ProgramSummary = {
 id: string;
 title: string;
 summary: string;
 durationDays: number;
};

export type Enrollment = {
 programId: string;
 currentDay: number;
 startedAt: string;
 completedAt: string | null;
 programTitle: string;
 durationDays: number;
 dayTheme: string;
 daySlug: string;
};

const DEMO_PROGRAM_ID = "33333333-3333-4333-8333-333333333301";

function demoProgram(): ProgramSummary {
  return {
    id: DEMO_PROGRAM_ID,
    title: t("programs.demoTitle"),
    summary: t("programs.demoSummary"),
    durationDays: 21,
  };
}

export async function listPrograms(): Promise<ProgramSummary[]> {
  const supabase = getSupabase();
  if (!supabase) return [demoProgram()];

  const { data } = await supabase
    .from("programs")
    .select("id, title, summary, duration_days")
    .eq("status", "published");

  if (!data || data.length === 0) return [demoProgram()];
  return data.map((p) => ({
    id: p.id,
    title: p.title,
    summary: p.summary,
    durationDays: p.duration_days,
  }));
}

export async function getActiveEnrollment(): Promise<Enrollment | null> {
  const supabase = getSupabase();
  if (!supabase) {
    const stored = localStorage.getItem("andrum.program.demo");
    if (!stored) return null;
    try {
      const parsed = JSON.parse(stored) as {
        currentDay: number;
        startedAt: string;
      };
      const demo = demoProgram();
      return {
        programId: demo.id,
        currentDay: parsed.currentDay,
        startedAt: parsed.startedAt,
        completedAt: null,
        programTitle: demo.title,
        durationDays: demo.durationDays,
        dayTheme: themeForProgramDay(parsed.currentDay),
        daySlug: slugForProgramDay(parsed.currentDay),
      };
    } catch {
      return null;
    }
  }

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return null;

  const { data } = await supabase
    .from("program_enrollments")
    .select("program_id, current_day, started_at, completed_at")
    .eq("user_id", user.id)
    .is("completed_at", null)
    .order("started_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (!data) return null;

  const { data: prog } = await supabase
    .from("programs")
    .select("title, duration_days")
    .eq("id", data.program_id)
    .maybeSingle();

  const day = data.current_day ?? 1;
  return {
    programId: data.program_id,
    currentDay: day,
    startedAt: data.started_at,
    completedAt: data.completed_at,
    programTitle: prog?.title ?? t("programs.title"),
    durationDays: prog?.duration_days ?? 21,
    dayTheme: themeForProgramDay(day),
    daySlug: slugForProgramDay(day),
  };
}

export async function enrollInProgram(
  programId: string
): Promise<
  { ok: true; enrollment: Enrollment } | { ok: false; message: string }
> {
  const supabase = getSupabase();
  if (!supabase) {
    const demo = demoProgram();
    const enrollment: Enrollment = {
      programId,
      currentDay: 1,
      startedAt: new Date().toISOString(),
      completedAt: null,
      programTitle: demo.title,
      durationDays: demo.durationDays,
      dayTheme: themeForProgramDay(1),
      daySlug: slugForProgramDay(1),
    };
    localStorage.setItem(
      "andrum.program.demo",
      JSON.stringify({ currentDay: 1, startedAt: enrollment.startedAt })
    );
    return { ok: true, enrollment };
  }

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) {
    return { ok: false, message: t("programs.loginRequired") };
  }

  const { error } = await supabase.from("program_enrollments").upsert(
    {
      user_id: user.id,
      program_id: programId,
      current_day: 1,
      completed_at: null,
      started_at: new Date().toISOString(),
    },
    { onConflict: "user_id,program_id" }
  );

  if (error) {
    return { ok: false, message: t("programs.startFail") };
  }

  await supabase
    .from("profiles")
    .update({ current_program_id: programId })
    .eq("id", user.id);

  const enrollment = await getActiveEnrollment();
  if (!enrollment) {
    return { ok: false, message: t("programs.startReadFail") };
  }
  return { ok: true, enrollment };
}

export async function advanceProgramDay(): Promise<
  { ok: true; enrollment: Enrollment | null } | { ok: false; message: string }
> {
  const current = await getActiveEnrollment();
  if (!current) {
    return { ok: false, message: t("programs.noActive") };
  }

  const nextDay = current.currentDay + 1;
  const done = nextDay > current.durationDays;

  const supabase = getSupabase();
  if (!supabase) {
    if (done) {
      localStorage.removeItem("andrum.program.demo");
      return { ok: true, enrollment: null };
    }
    localStorage.setItem(
      "andrum.program.demo",
      JSON.stringify({
        currentDay: nextDay,
        startedAt: current.startedAt,
      })
    );
    const enrollment = await getActiveEnrollment();
    return { ok: true, enrollment };
  }

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return { ok: false, message: t("programs.loginNeed") };

  if (done) {
    await supabase
      .from("program_enrollments")
      .update({
        completed_at: new Date().toISOString(),
        current_day: current.durationDays,
      })
      .eq("user_id", user.id)
      .eq("program_id", current.programId);
    return { ok: true, enrollment: null };
  }

  const { error } = await supabase
    .from("program_enrollments")
    .update({ current_day: nextDay })
    .eq("user_id", user.id)
    .eq("program_id", current.programId);

  if (error) return { ok: false, message: t("programs.updateFail") };
  const enrollment = await getActiveEnrollment();
  return { ok: true, enrollment };
}

/**
 * Start experiment for program day.
 * @param dayOverride, past/selected day slug; default = enrollment.currentDay
 */
export async function startProgramDayExperiment(
  dayOverride?: number
): Promise<{
  assignment: ExperimentAssignment | null;
  experiment: Experiment | null;
}> {
  const enrollment = await getActiveEnrollment();
  if (!enrollment) return { assignment: null, experiment: null };

  const day = dayOverride ?? enrollment.currentDay;
  // Don't start future days as full assign unless user is at/ past that day
  if (day > enrollment.currentDay) {
    return { assignment: null, experiment: null };
  }

  const catalog = await listPublishedExperiments();
  const slug = slugForProgramDay(day);
  const match =
    catalog.find((e) => e.slug === slug) ?? catalog[0] ?? null;
  if (!match) return { assignment: null, experiment: null };

  return assignExperiment(match.id, {
    reasonCode: "program",
    reasonLabel: `${t("experiment.reasons.program")} · ${themeForProgramDay(day)}`,
  });
}
